import {
  Body1,
  Button,
  Dropdown,
  Field,
  Input,
  Option,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Add20Filled,
  Add20Regular,
  Delete20Filled,
  Delete20Regular,
  bundleIcon,
} from '@fluentui/react-icons';

import { classifyBindingFlowDiagnostics } from '../../bindingFlow.utils.js';
import { useStyles as useFormStyles } from '../../EvaluationTemplateForm.style.js';
import {
  VARIABLE_NAME_PATTERN,
  VARIABLE_TYPE_OPTIONS,
} from '../../EvaluationTemplateForm.types.js';

import type { BindingFlowPanelProps } from './BindingFlowPanel.interface.js';
import { useStyles } from './BindingFlowPanel.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const AddIcon = bundleIcon(Add20Filled, Add20Regular);
const DeleteIcon = bundleIcon(Delete20Filled, Delete20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

function formatInputValue(value: unknown): string {
  if (value === null) return 'null';
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }
  return JSON.stringify(value);
}

export function BindingFlowPanel({
  analysis,
  bindings,
  onBindingChange,
  onVariableUpdate,
  onVariableAdd,
  onVariableRemove,
  disabled = false,
  viewOnly = false,
}: BindingFlowPanelProps) {
  const formStyles = useFormStyles();
  const styles = useStyles();
  const diagnostics = classifyBindingFlowDiagnostics(analysis);
  const templateNameCounts = new Map<string, number>();
  for (const binding of bindings) {
    if (
      binding.templateVariableId !== undefined &&
      binding.name.trim().length > 0
    ) {
      const name = binding.name.trim();
      templateNameCounts.set(name, (templateNameCounts.get(name) ?? 0) + 1);
    }
  }

  return (
    <section className={formStyles.card} aria-labelledby="card-binding-flow">
      <div id="card-binding-flow" className={formStyles.cardLabel}>
        Evaluation parameters
      </div>
      <div className={formStyles.cardBody}>
        {diagnostics.components.length > 0 && (
          <div className={styles.issueList} aria-label="Binding diagnostics">
            {diagnostics.components.map((message) => (
              <div
                key={message}
                className={mergeClasses(styles.issue, styles.error)}
                role="alert"
              >
                {message}
              </div>
            ))}
          </div>
        )}

        <div className={styles.section}>
          {bindings.length === 0 ? (
            <Body1 className={styles.empty}>No inputs.</Body1>
          ) : (
            <div className={styles.bindingRows}>
              <div className={styles.bindingHeader} aria-hidden="true">
                <span>Name</span>
                <span>Type</span>
                <span>Default</span>
                <span aria-hidden />
              </div>
              {bindings.map((binding) => {
                const templateVariable =
                  binding.templateVariableId !== undefined;
                const inputErrors = diagnostics.inputs[binding.name] ?? [];
                const variableNameError = !templateVariable
                  ? undefined
                  : binding.name.trim().length === 0
                    ? 'Name is required.'
                    : !VARIABLE_NAME_PATTERN.test(binding.name.trim())
                      ? 'Use UPPER_SNAKE_CASE.'
                      : (templateNameCounts.get(binding.name.trim()) ?? 0) > 1
                        ? 'Duplicate variable name.'
                        : undefined;
                return (
                  <div
                    key={binding.templateVariableId ?? binding.name}
                    className={styles.bindingRow}
                  >
                    <Field
                      validationState={
                        variableNameError === undefined ? 'none' : 'error'
                      }
                      validationMessage={variableNameError}
                    >
                      <Input
                        value={binding.name}
                        placeholder={templateVariable ? 'VARIABLE_NAME' : ''}
                        disabled={!templateVariable || disabled || viewOnly}
                        onChange={(_, data) => {
                          if (binding.templateVariableId !== undefined) {
                            onVariableUpdate(binding.templateVariableId, {
                              name: data.value,
                            });
                          }
                        }}
                        aria-label={
                          binding.name.length > 0
                            ? `Input name ${binding.name}`
                            : 'Variable name'
                        }
                      />
                    </Field>
                    {templateVariable ? (
                      <Dropdown
                        value={binding.type ?? ''}
                        selectedOptions={
                          binding.type === undefined ? [] : [binding.type]
                        }
                        disabled={disabled || viewOnly}
                        onOptionSelect={(_, data) => {
                          if (
                            binding.templateVariableId !== undefined &&
                            data.optionValue !== undefined
                          ) {
                            onVariableUpdate(binding.templateVariableId, {
                              type: data.optionValue as
                                | 'string'
                                | 'number'
                                | 'integer'
                                | 'boolean',
                            });
                          }
                        }}
                        aria-label={
                          binding.name.length > 0
                            ? `Type for ${binding.name}`
                            : 'Variable type'
                        }
                      >
                        {VARIABLE_TYPE_OPTIONS.map((option) => (
                          <Option
                            key={option.value}
                            value={option.value}
                            text={option.label}
                          >
                            {option.label}
                          </Option>
                        ))}
                      </Dropdown>
                    ) : (
                      <Input
                        value={binding.type ?? ''}
                        disabled
                        aria-label={`Type for ${binding.name}`}
                      />
                    )}
                    <Field
                      validationState={
                        inputErrors.length > 0 ? 'error' : 'none'
                      }
                      validationMessage={inputErrors.join(' ')}
                    >
                      <Input
                        value={
                          binding.expr.length > 0
                            ? binding.expr
                            : binding.defaultValue === undefined
                              ? ''
                              : formatInputValue(binding.defaultValue)
                        }
                        placeholder={
                          binding.name.length === 0
                            ? 'Name required'
                            : binding.defaultValue === undefined
                              ? 'Required'
                              : ''
                        }
                        disabled={
                          disabled ||
                          viewOnly ||
                          (!templateVariable && binding.name.length === 0)
                        }
                        onChange={(_, data) => {
                          if (
                            binding.templateVariableId !== undefined &&
                            binding.expr.length === 0
                          ) {
                            onVariableUpdate(binding.templateVariableId, {
                              defaultValue: data.value,
                            });
                          } else {
                            onBindingChange(binding.name, data.value);
                          }
                        }}
                        aria-label={`Default for ${binding.name}`}
                      />
                    </Field>
                    {templateVariable && !disabled && !viewOnly ? (
                      <Button
                        appearance="subtle"
                        icon={<DeleteIcon />}
                        onClick={() => {
                          if (binding.templateVariableId !== undefined) {
                            onVariableRemove(binding.templateVariableId);
                          }
                        }}
                        aria-label={`Remove variable ${binding.name || 'new variable'}`}
                      />
                    ) : (
                      <span aria-hidden />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!disabled && !viewOnly && (
          <div>
            <Button icon={<AddIcon />} onClick={onVariableAdd}>
              Add variable
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
